import apiClient from "../utils/axios";
import { StripeCheckoutResponse, StripeSubscriptionStatus } from "../utils/stripe";

export interface CheckoutRequest {
  billing_cycle: 'monthly' | 'annual';
  success_url: string;
  cancel_url: string;
  billing_mode?: 'CreditCard' | 'Invoice';
}

export class StripeService {
  
  static async createCheckoutSession(
    organizationId: string, 
    checkoutRequest: CheckoutRequest,
    token: string
  ): Promise<StripeCheckoutResponse> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/checkout`,
      checkoutRequest,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async getSubscriptionStatus(
    organizationId: string,
    token: string
  ): Promise<StripeSubscriptionStatus> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/subscription-status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async cancelSubscription(
    organizationId: string,
    token: string
  ): Promise<{ status: string; message: string; subscription_id: string }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/cancel-subscription`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static redirectToCheckout(checkoutUrl: string): void {
    window.location.href = checkoutUrl;
  }

  static async createCustomerPortalSession(
    organizationId: string,
    token: string
  ): Promise<{ portal_url: string }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/customer-portal`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static redirectToCustomerPortal(portalUrl: string): void {
    window.location.href = portalUrl;
  }

  static async getCustomerInfo(
    organizationId: string,
    token: string
  ): Promise<{ customer_email: string | null; customer_name: string | null; has_customer: boolean; customer_id: string | null }> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/customer-info`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async updateCustomerEmail(
    organizationId: string,
    email: string,
    token: string
  ): Promise<{ success: boolean; message: string; customer_email: string; customer_id: string }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/update-customer-email`,
      { email },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async syncSubscriptionWithStripe(
    organizationId: string,
    token: string
  ): Promise<{ success: boolean; message: string; changes_made: string[] }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/subscription/sync`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }
} 